import React from 'react';

function DataExport({ data }) {
  const downloadJSON = () => {
    const dataStr = JSON.stringify(data, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `experiment_data_${data.participantID}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };
  
  const downloadCSV = () => {
    // Convert trials data to CSV format
    const csvRows = [];
    
    // Header - Updated with new fields
    csvRows.push([
      'participantID',
      'age',
      'gender',
      'expertise',
      'artCourses',
      'museumVisits',
      'artPractice',
      'blockNumber',
      'artist',
      'trialNumber',
      'trialFamily',  // New: T1, T2, T3, T4, practice, catch
      'trialType',
      'leftType',
      'rightType',
      'leftModel',     // New: AI model ID
      'rightModel',    // New: AI model ID
      'choice',
      'chosenType',
      'choiceRT',
      'confidence',    // New: 1-6 scale
      'diagnosticReason',
      'subjectCategory',  // New: subject category tag
      'composition',      // New: composition tag
      'isPractice',       // New: practice trial flag
      'isCatch',          // New: catch trial flag
      'isRepeated',       // New: repeated trial flag
      'timestamp'
    ].join(','));
    
    // Data rows (handle empty blocks)
    if (data.blocks && data.blocks.length > 0) {
      data.blocks.forEach((block, blockIndex) => {
        if (block.trials && block.trials.length > 0) {
          block.trials.forEach(trial => {
            csvRows.push([
              data.participantID,
              data.demographics.age || '',
              data.demographics.gender || '',
              data.demographics.expertise || '',
              data.demographics.artCourses || '',
              data.demographics.museumVisits || '',
              data.demographics.artPractice || '',
              blockIndex + 1,
              block.artist,
              trial.trialNumber || '',
              trial.trialFamily || '',      // T1-T4, practice, catch
              trial.trialType || trial.type || '',
              trial.leftType || '',
              trial.rightType || '',
              trial.leftModel || '',
              trial.rightModel || '',
              trial.choice || '',
              trial.chosenType || '',
              trial.choiceRT || '',
              trial.confidence || '',        // 6-point confidence
              trial.diagnosticReason || '',
              trial.subjectCategory || '',
              trial.composition || '',
              trial.trialFamily === 'practice' ? '1' : '0',
              trial.trialFamily === 'catch' ? '1' : '0',
              trial.isRepeated ? '1' : '0',
              trial.timestamp || ''
            ].join(','));
          });
        }
      });
    }
    
    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `experiment_data_${data.participantID}.csv`);
    link.click();
  };
  
  const viewData = () => {
    console.log('Experiment Data:', data);
    alert('Data has been logged to the browser console. Press F12 to view.');
  };
  
  // Calculate summary statistics (handle empty data)
  const totalTrials = data.blocks && data.blocks.length > 0 
    ? data.blocks.reduce((sum, block) => sum + (block.trials ? block.trials.length : 0), 0)
    : 0;
  
  const avgRT = data.blocks && data.blocks.length > 0 
    ? data.blocks.reduce((sum, block) => {
        if (block.trials && block.trials.length > 0) {
          const blockRT = block.trials.reduce((s, t) => s + (t.choiceRT || 0), 0) / block.trials.length;
          return sum + blockRT;
        }
        return sum;
      }, 0) / data.blocks.length
    : 0;
  
  // Calculate average confidence
  const avgConfidence = data.blocks && data.blocks.length > 0 
    ? data.blocks.reduce((sum, block) => {
        if (block.trials && block.trials.length > 0) {
          const validConfidences = block.trials.filter(t => t.confidence).map(t => t.confidence);
          if (validConfidences.length > 0) {
            const blockConf = validConfidences.reduce((s, c) => s + c, 0) / validConfidences.length;
            return sum + blockConf;
          }
        }
        return sum;
      }, 0) / data.blocks.filter(b => b.trials && b.trials.some(t => t.confidence)).length
    : 0;
  
  // Count trial families
  const trialFamilyCounts = {};
  if (data.blocks && data.blocks.length > 0) {
    data.blocks.forEach(block => {
      if (block.trials && block.trials.length > 0) {
        block.trials.forEach(trial => {
          const family = trial.trialFamily || 'unknown';
          trialFamilyCounts[family] = (trialFamilyCounts[family] || 0) + 1;
        });
      }
    });
  }
  
  return (
    <div className="phase-container export-container">
      <h1>Experiment Complete!</h1>
      
      <div className="completion-message">
        <p>Thank you for participating in this style perception study!</p>
        <p>Your responses have been recorded successfully.</p>
      </div>
      
      <div style={{ margin: '30px 0', padding: '20px', background: '#f9f9f9', borderRadius: '8px' }}>
        <h2>Summary</h2>
        <p><strong>Participant ID:</strong> {data.participantID}</p>
        <p><strong>Total Trials Completed:</strong> {totalTrials}</p>
        <p><strong>Number of Artists:</strong> {data.blocks.length}</p>
        <p><strong>Average Response Time:</strong> {Math.round(avgRT)} ms</p>
        <p><strong>Average Confidence:</strong> {avgConfidence.toFixed(1)}/6</p>
        <p><strong>Expertise Level:</strong> {data.demographics.expertise}/7</p>
        
        {Object.keys(trialFamilyCounts).length > 0 && (
          <div style={{ marginTop: '10px' }}>
            <strong>Trial Distribution:</strong>
            <ul style={{ marginLeft: '20px', marginTop: '5px' }}>
              {Object.entries(trialFamilyCounts).map(([family, count]) => (
                <li key={family}>{family}: {count} trials</li>
              ))}
            </ul>
          </div>
        )}
      </div>
      
      <h2>Download Your Data</h2>
      <p>You can download your experimental data in different formats:</p>
      
      <div className="export-buttons">
        <button onClick={downloadJSON}>
          Download JSON
        </button>
        <button onClick={downloadCSV}>
          Download CSV
        </button>
        <button onClick={viewData}>
          View in Console
        </button>
      </div>
      
      <div style={{ marginTop: '40px', padding: '20px', background: '#fff3cd', borderRadius: '8px' }}>
        <p style={{ fontWeight: 'bold' }}>Important:</p>
        <p>Please save your data file and send it to the research team at research@example.edu</p>
      </div>
    </div>
  );
}

export default DataExport;